import React, { Fragment, useCallback, useState } from "react";
import { useEffectAsync } from "../../../../../common/hooks/use-effect-async";
import { CircularProgress, FormControl, FormLabel, MenuItem, Select, TextField } from "@mui/material";
import { t } from "i18next";
import { PalletShippingInformationSelectItem } from "./pallet-shipping-information-select-item";
import { usePalletShippingContext, usePalletShippingContextDispatcher } from "../pallet-shipping-context";
import moment from "moment";

export enum ShippingInformationType {
    PickupSlot = "pickupSlots",
}

interface IPalletShippingInformationSelect {
    shippingInformationType: ShippingInformationType;
    loadData: () => Promise<PalletShippingInformationSelectItem[]>;
}

export const PalletShippingInformationSelect = (props: IPalletShippingInformationSelect) => {
    const [shippingInformationList, setShippingInformationList] = useState<PalletShippingInformationSelectItem[]>([]);
    const [shippingId, setShippingId] = useState("placeholder");
    const [loadingData, setLoadingData] = useState(true);

    const {
        comment,
        shippingInformation: { containerSealNumber },
    } = usePalletShippingContext();

    const palletShippingContextDispatcher = usePalletShippingContextDispatcher();

    useEffectAsync(async () => {
        palletShippingContextDispatcher({ action: "initialize" });
        setShippingInformationList(await props.loadData());
        setLoadingData(false);
    }, []);

    const handleOnSelect = useCallback((id: string) => {
        setShippingId(id);
        const shippingInformation = shippingInformationList?.find(si => si.id === id);

        if (shippingInformation) {
            palletShippingContextDispatcher({
                action: "setShippingInformation",
                shippingInformation: {
                    id: shippingInformation.id,
                    pickupSlotName: shippingInformation.pickupSlotName,
                    containerSealNumber: shippingInformation.containerSealNumber ?? containerSealNumber,
                    truckIdentifier: shippingInformation.truckIdentifier,
                    pickingDate: moment(shippingInformation.pickingDate),
                },
            });
        }
    }, [props.shippingInformationType, shippingInformationList]);

    return <Fragment>
        <div className={"pallet-shipping-form__container pallet-shipping-form__container--full-width"}>
            <div>
                <FormLabel htmlFor={"shipping-information-select"}>
                    {t("components.pallet.shipping.pickupSlot")}
                </FormLabel>
                <div className={"pallet-shipping-form__select"}>
                    <Select data-testid={"ship-pallet-shipping-information-select"}
                            inputProps={{id: "shipping-information-select"}}
                            value={shippingId}
                            disabled={shippingInformationList.length === 0}>
                        {loadingData &&
                            <MenuItem value={"placeholder"} key={"shipping-information-loading"}>
                                <div className={"pallet-shipping-form__select-item--loading"}>
                                    <CircularProgress size={18}/>
                                    {t(`components.pallet.shipping.${props.shippingInformationType}.informationLoading`)}
                                </div>
                            </MenuItem>}
                        {!loadingData && shippingInformationList.length === 0 &&
                            <MenuItem value={"placeholder"} key={"shipping-information-loading"}>
                                {t(`components.pallet.shipping.${props.shippingInformationType}.informationNotFound`)}
                            </MenuItem>}
                        {shippingInformationList.map(si =>
                            <MenuItem data-testid={`shipping-information-select-item-${si.id}`}
                                      onClick={() => handleOnSelect(si.id)}
                                      key={si.id}
                                      value={si.id}>
                                {si.formatted}
                            </MenuItem>)
                        }
                    </Select>
                </div>
            </div>
        </div>
        <div className={"pallet-shipping-form__container pallet-shipping-form__container--full-width"}>
            <div>
                <FormControl variant={"outlined"} fullWidth>
                    <FormLabel htmlFor={"closing-comment"}>
                        {t("components.pallet.shipping.comment")}
                    </FormLabel>
                    <TextField id={"shipping-comment"}
                               variant={"outlined"}
                               data-testid={"shipping-comment"}
                               multiline
                               minRows={3}
                               maxRows={5}
                               slotProps={{ htmlInput: { maxLength: 255 } }}
                               onChange={event => palletShippingContextDispatcher({
                                   action: "setComment",
                                   comment: event.target.value,
                               })}
                               value={comment} />
                </FormControl>
            </div>
        </div>
    </Fragment>;
};
